/* ==========================================================
   SNABITTY GLOBAL STOCK CONTROLLER — HARD LOCK VERSION
   Asset name: snab-stock-controller.js

   Requires this to load before it:
   {% render 'snab-stock-config' %}

   What it does:
   - Reads window.SNAB_STOCK.outOfStockSizes
   - Disables out-of-stock Series + Size choices on builder/set/gift pages
   - Re-applies the disabled state after Shopify's own variant picker refreshes
   - Blocks clicks/submits for out-of-stock combinations
   ========================================================== */

(function () {
  'use strict';

  const STOCK = window.SNAB_STOCK || {};

  const DEFAULT_SIZE_OPTIONS_BY_SERIES = {
    '12': ['Base', 'Pro', 'Pro Max', 'Mini'],
    '13': ['Base', 'Pro', 'Pro Max', 'Mini'],
    '14': ['Base', 'Pro', 'Pro Max', 'Plus'],
    '15': ['Base', 'Pro', 'Pro Max', 'Plus'],
    '16': ['Base', 'Pro', 'Pro Max', 'Plus', 'E'],
    '17': ['Base', 'Air', 'Pro', 'Pro Max']
  };

  const OUT_OF_STOCK = STOCK.outOfStockSizes || window.outOfStockSizes || {};
  const SIZE_OPTIONS_BY_SERIES = (STOCK.sizeOptionsBySeries && Object.keys(STOCK.sizeOptionsBySeries).length)
    ? STOCK.sizeOptionsBySeries
    : ((window.sizeOptionsBySeries && Object.keys(window.sizeOptionsBySeries).length) ? window.sizeOptionsBySeries : DEFAULT_SIZE_OPTIONS_BY_SERIES);
  const CASE_VARIANT_MAP = STOCK.caseVariantMap || window.SNAB_CASE_VARIANT_MAP || {};

  // Backward compatibility for the existing builder page.
  window.outOfStockSizes = OUT_OF_STOCK;
  window.sizeOptionsBySeries = SIZE_OPTIONS_BY_SERIES;
  window.SNAB_CASE_VARIANT_MAP = CASE_VARIANT_MAP;

  const SERIES_VALUES = ['12', '13', '14', '15', '16', '17'];
  const SIZE_VALUES = ['Base', 'Pro', 'Pro Max', 'Mini', 'Plus', 'Air', 'E'];

  const SELECTORS = {
    productForm: 'product-form form, form[action*="/cart/add"]',
    addButton: 'button[name="add"], button[type="submit"], .product-form__submit, .shopify-payment-button__button'
  };

  let updateQueued = false;
  let lastTouchedAt = 0;

  function normalize(value) {
    return String(value || '')
      .trim()
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  function normalizeKey(value) {
    return normalize(value).replace(/[^a-z0-9]/g, '');
  }

  function displayValue(el) {
    if (!el) return '';
    if (el.tagName === 'OPTION') return el.textContent || el.value || '';
    if (el.tagName === 'INPUT') return el.value || el.getAttribute('data-value') || '';
    return el.getAttribute('data-value') || el.value || el.textContent || '';
  }

  function isSeriesValue(value) {
    const v = normalizeKey(value).replace(/^iphone/, '');
    return SERIES_VALUES.includes(v);
  }

  function isSizeValue(value) {
    const v = normalizeKey(value);
    return SIZE_VALUES.some(size => normalizeKey(size) === v);
  }

  function cleanSeries(value) {
    const raw = String(value || '').trim();
    const fromText = raw.match(/(?:iphone\s*)?(12|13|14|15|16|17)/i);
    return fromText ? fromText[1] : raw.replace(/[^0-9]/g, '');
  }

  function cleanSize(value) {
    const raw = String(value || '').trim();
    const key = normalizeKey(raw);
    const match = SIZE_VALUES.find(size => normalizeKey(size) === key);
    if (match) return match;
    if (key === 'base') return 'Base';
    if (key === 'promax') return 'Pro Max';
    if (key === 'mini') return 'Mini';
    if (key === 'plus') return 'Plus';
    if (key === 'air') return 'Air';
    if (key === 'e') return 'E';
    return raw;
  }

  function isValidSizeForSeries(series, size) {
    const s = cleanSeries(series);
    const z = cleanSize(size);

    // If no map exists for a series, do not hide anything.
    // This keeps the controller safe on unexpected products/pages.
    if (!Array.isArray(SIZE_OPTIONS_BY_SERIES[s])) return true;

    return SIZE_OPTIONS_BY_SERIES[s].includes(z);
  }

  function isOutOfStock(series, size) {
    const s = cleanSeries(series);
    const z = cleanSize(size);
    return Array.isArray(OUT_OF_STOCK[s]) && OUT_OF_STOCK[s].includes(z);
  }

  function findOptionGroups(root) {
    const groups = [];

    root.querySelectorAll('fieldset').forEach(fieldset => {
      const legend = fieldset.querySelector('legend, .form__label, [class*="label"]');
      const labelText = normalize(legend ? legend.textContent : '');
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      if (!inputs.length) return;

      const values = inputs.map(displayValue);
      const looksLikeSeries = labelText.includes('series') || values.some(isSeriesValue);
      const looksLikeSize = labelText.includes('size') || values.some(isSizeValue);

      if (looksLikeSeries || looksLikeSize) {
        groups.push({
          type: looksLikeSeries && !looksLikeSize ? 'series' : looksLikeSize ? 'size' : '',
          element: fieldset,
          inputs
        });
      }
    });

    root.querySelectorAll('select').forEach(select => {
      const name = normalize(select.name || select.id || select.getAttribute('aria-label') || '');
      const values = Array.from(select.options).map(displayValue);
      const looksLikeSeries = name.includes('series') || values.some(isSeriesValue);
      const looksLikeSize = name.includes('size') || values.some(isSizeValue);

      if (looksLikeSeries || looksLikeSize) {
        groups.push({
          type: looksLikeSeries && !looksLikeSize ? 'series' : looksLikeSize ? 'size' : '',
          element: select,
          select,
          inputs: Array.from(select.options)
        });
      }
    });

    return groups;
  }

  function guessSeriesAndSizeGroups(groups) {
    const seriesGroup = groups.find(g => g.type === 'series') || groups.find(g => g.inputs.some(i => isSeriesValue(displayValue(i))));
    const sizeGroup = groups.find(g => g.type === 'size' && g !== seriesGroup) || groups.find(g => g !== seriesGroup && g.inputs.some(i => isSizeValue(displayValue(i))));
    return { seriesGroup, sizeGroup };
  }

  function selectedValue(group) {
    if (!group) return '';
    if (group.select) return group.select.value || group.select.options[group.select.selectedIndex]?.textContent || '';
    const checked = group.inputs.find(input => input.checked);
    return checked ? displayValue(checked) : displayValue(group.inputs[0]);
  }

  function findVisibleForInput(input) {
    if (!input) return null;
    if (input.tagName === 'OPTION') return input;
    if (input.id) {
      const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (label) return label;
    }
    return input.closest('label') || input;
  }

  function setInputState(input, state) {
    if (!input) return;

    const hidden = !!(state && state.hidden);
    const disabled = !!(state && state.disabled);
    const invalid = !!(state && state.invalid);

    if (input.tagName === 'OPTION') {
      input.hidden = hidden;
      input.disabled = hidden || disabled;
      input.setAttribute('data-snab-stock-hidden', hidden ? 'true' : 'false');
      input.setAttribute('data-snab-stock-disabled', disabled ? 'true' : 'false');
      input.setAttribute('data-snab-stock-invalid', invalid ? 'true' : 'false');
      return;
    }

    const visible = findVisibleForInput(input);

    input.disabled = hidden || disabled;
    input.setAttribute('aria-disabled', hidden || disabled ? 'true' : 'false');
    input.setAttribute('data-snab-stock-hidden', hidden ? 'true' : 'false');
    input.setAttribute('data-snab-stock-disabled', disabled ? 'true' : 'false');
    input.setAttribute('data-snab-stock-invalid', invalid ? 'true' : 'false');

    if (visible) {
      visible.classList.toggle('snab-stock-hidden', hidden);
      visible.classList.toggle('snab-stock-disabled', disabled && !hidden);
      visible.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      visible.setAttribute('aria-disabled', hidden || disabled ? 'true' : 'false');
      visible.setAttribute('data-snab-stock-hidden', hidden ? 'true' : 'false');
      visible.setAttribute('data-snab-stock-disabled', disabled ? 'true' : 'false');
      visible.setAttribute('data-snab-stock-invalid', invalid ? 'true' : 'false');
    }
  }

  function showStockMessage(form, message) {
    let msg = form.querySelector('[data-snab-stock-message]');
    if (!msg) {
      msg = document.createElement('div');
      msg.setAttribute('data-snab-stock-message', '');
      msg.className = 'snab-stock-message';
      const add = form.querySelector(SELECTORS.addButton);
      if (add && add.parentNode) add.parentNode.insertBefore(msg, add);
      else form.appendChild(msg);
    }
    msg.textContent = message || '';
    msg.hidden = !message;
  }

  function setAddDisabled(form, disabled, series, size) {
    const buttons = Array.from(form.querySelectorAll(SELECTORS.addButton));
    buttons.forEach(btn => {
      btn.disabled = !!disabled;
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      btn.classList.toggle('snab-stock-add-disabled', !!disabled);
    });

    if (disabled) {
      const label = size === 'Base' ? `iPhone ${series}` : `iPhone ${series} ${size}`;
      showStockMessage(form, `${label} is currently out of stock.`);
    } else {
      showStockMessage(form, '');
    }
  }

  function updateForm(form) {
    const root = form.closest('.shopify-section') || form.closest('main') || document;
    const groups = findOptionGroups(root);
    const { seriesGroup, sizeGroup } = guessSeriesAndSizeGroups(groups);

    if (!seriesGroup || !sizeGroup) return;

    const currentSeries = cleanSeries(selectedValue(seriesGroup));

    sizeGroup.inputs.forEach(sizeInput => {
      const size = cleanSize(displayValue(sizeInput));
      if (!size) return;

      const validForSeries = isValidSizeForSeries(currentSeries, size);
      const outOfStock = isOutOfStock(currentSeries, size);

      // Invalid sizes do not exist for that iPhone series, so hide them.
      // Existing but unavailable sizes remain visible, greyed out, and blocked.
      setInputState(sizeInput, {
        hidden: !validForSeries,
        invalid: !validForSeries,
        disabled: validForSeries && outOfStock
      });
    });

    const currentSize = cleanSize(selectedValue(sizeGroup));
    const selectedIsInvalid = !isValidSizeForSeries(currentSeries, currentSize);
    const selectedIsOut = isOutOfStock(currentSeries, currentSize);

    if (selectedIsInvalid) {
      setAddDisabled(form, true, currentSeries, currentSize);
      showStockMessage(form, `Please choose an available size for iPhone ${currentSeries}.`);
    } else {
      setAddDisabled(form, selectedIsOut, currentSeries, currentSize);
    }
  }

  function updateAll() {
    document.querySelectorAll(SELECTORS.productForm).forEach(updateForm);
  }

  function scheduleUpdate() {
    if (updateQueued) return;
    updateQueued = true;
    requestAnimationFrame(() => {
      updateQueued = false;
      updateAll();
    });
  }

  function hardRefreshForOneSecond() {
    // Shopify's variant picker often rewrites classes/disabled states after click/change.
    // These delayed updates keep Snabitty stock as the final authority.
    [0, 50, 120, 250, 500, 900, 1300].forEach(ms => window.setTimeout(updateAll, ms));
  }

  function injectStyles() {
    if (document.getElementById('snab-stock-controller-styles')) return;
    const style = document.createElement('style');
    style.id = 'snab-stock-controller-styles';
    style.textContent = `
      .snab-stock-hidden,
      [data-snab-stock-hidden="true"],
      [data-snab-stock-invalid="true"],
      input[data-snab-stock-hidden="true"] + label,
      input[data-snab-stock-invalid="true"] + label {
        display: none !important;
      }
      .snab-stock-disabled,
      [data-snab-stock-disabled="true"] {
        opacity: .38 !important;
        cursor: not-allowed !important;
        text-decoration: line-through !important;
      }
      label.snab-stock-disabled,
      label[data-snab-stock-disabled="true"] {
        pointer-events: auto !important;
      }
      input[data-snab-stock-disabled="true"] + label,
      input:disabled + label {
        opacity: .38 !important;
        cursor: not-allowed !important;
        text-decoration: line-through !important;
      }
      .snab-stock-message {
        margin: 10px 0 12px;
        padding: 10px 12px;
        border-radius: 12px;
        background: #fff1ef;
        color: #9f2d25;
        font-size: 13px;
        line-height: 1.35;
        font-weight: 600;
      }
      .snab-stock-add-disabled {
        opacity: .52 !important;
        cursor: not-allowed !important;
      }
    `;
    document.head.appendChild(style);
  }

  function bind() {
    injectStyles();
    updateAll();
    hardRefreshForOneSecond();

    document.addEventListener('change', function (event) {
      if (event.target.matches('input[type="radio"], select')) {
        lastTouchedAt = Date.now();
        hardRefreshForOneSecond();
      }
    }, true);

    document.addEventListener('click', function (event) {
      const blocked = event.target.closest('[data-snab-stock-disabled="true"], .snab-stock-disabled, [data-snab-stock-hidden="true"], .snab-stock-hidden');
      if (blocked) {
        event.preventDefault();
        event.stopPropagation();
        hardRefreshForOneSecond();
        return false;
      }
      if (event.target.closest('variant-radios, variant-selects, .product-form__input, fieldset')) {
        lastTouchedAt = Date.now();
        hardRefreshForOneSecond();
      }
    }, true);

    document.addEventListener('submit', function (event) {
      const form = event.target.closest(SELECTORS.productForm);
      if (!form) return;
      updateForm(form);
      const disabledButton = form.querySelector(`${SELECTORS.addButton}.snab-stock-add-disabled`);
      if (disabledButton) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    ['variant:change', 'variantChange', 'shopify:section:load', 'shopify:section:select'].forEach(name => {
      document.addEventListener(name, hardRefreshForOneSecond, true);
    });

    const observer = new MutationObserver(() => {
      // Avoid doing heavy work on every tiny mutation. Shopify changes attributes a lot.
      scheduleUpdate();
      if (Date.now() - lastTouchedAt < 1800) hardRefreshForOneSecond();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled', 'checked', 'data-snab-stock-disabled', 'data-snab-stock-hidden']
    });
  }

  window.SNAB_STOCK_HELPERS = {
    isOutOfStock,
    isValidSizeForSeries,
    cleanSeries,
    cleanSize,
    updateAll,
    hardRefreshForOneSecond,
    caseVariantMap: CASE_VARIANT_MAP,
    outOfStockSizes: OUT_OF_STOCK,
    sizeOptionsBySeries: SIZE_OPTIONS_BY_SERIES
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
